import { z } from 'zod';

export const PartyTypeSchema = z.enum(['customer', 'vendor', 'both']);
export type PartyType = z.infer<typeof PartyTypeSchema>;

export const AddressSchema = z.object({
  line1: z.string().max(200),
  line2: z.string().max(200).optional(),
  city: z.string().max(100),
  region: z.string().max(100).optional(),
  postalCode: z.string().max(32).optional(),
  country: z.string().length(2).regex(/^[A-Z]{2}$/),
});
export type Address = z.infer<typeof AddressSchema>;

export const PartySchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  code: z.string().min(1).max(64),
  displayName: z.string().min(1).max(200),
  partyType: PartyTypeSchema,
  email: z.string().email().nullable(),
  phone: z.string().max(32).nullable(),
  billingAddress: AddressSchema.nullable(),
  shippingAddress: AddressSchema.nullable(),
  taxId: z.string().max(64).nullable(),
  paymentTermsDays: z.number().int().min(0).max(365),
  creditLimit: z.string().regex(/^-?\d+(\.\d{1,4})?$/).nullable(),
  creditCurrencyCode: z.string().length(3).regex(/^[A-Z]{3}$/).nullable(),
  arAccountId: z.string().uuid().nullable(),
  apAccountId: z.string().uuid().nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  createdByUserId: z.string().uuid().nullable(),
  createdByName: z.string().nullable(),
  priceListId: z.string().uuid().nullable(),
  /** A self-requested customer type (price list) awaiting staff approval; null once approved/rejected. */
  requestedPriceListId: z.string().uuid().nullable(),
  isActive: z.boolean(),
  /**
   * Home branch (warehouse). null = SHARED: every branch can find and sell to
   * this customer, and each sees only what the customer owes it. Set = the
   * customer belongs to that branch; people confined to other branches do not
   * see them. Debt figures are per branch either way.
   */
  warehouseId: z.string().uuid().nullable(),
  warehouseName: z.string().nullable(),
  /** Resolved price-list ("customer type") name — always present on party reads. */
  priceListName: z.string().nullable().optional(),
  /**
   * The customer group's OWN currency (`price_lists.currency_code`) — the currency
   * this customer's prices are shown AND charged in on every surface (POS,
   * documents, mandob, storefront, offline terminal). null ⇒ no group currency,
   * so the tenant base currency applies. Prices are never converted; a group in a
   * foreign currency simply stores its prices in that currency.
   */
  priceListCurrencyCode: z.string().length(3).regex(/^[A-Z]{3}$/).nullable().optional(),
  /** Outstanding AR per currency (issued invoices' total − amount_paid). Present only on list({ metrics }). */
  debts: z.array(z.object({ currencyCode: z.string(), amount: z.string() })).optional(),
  /**
   * Whether this customer can sign in to the app/portal at all. Present only on
   * list({ portal }) and on the single-party read — the login row lives in the
   * PLATFORM database, so it costs a cross-database lookup and is opt-in.
   */
  portalEnabled: z.boolean().optional(),
  /** Whether that login also sees the read-only manager area. Same opt-in. */
  isAppManager: z.boolean().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type Party = z.infer<typeof PartySchema>;

/**
 * Per-related-table counts of what a hard purge would delete for a party.
 * Every field is a non-negative count (rows that would be removed), except
 * `portalLogin` (the customer has a portal login), `cashbackBalance` (the
 * money-valued wallet balance in base currency, if any) and `outstandingDebts`
 * (issued-invoice AR per currency). Used both by the delete-preview (drives the
 * danger dialog) and the purge response's `deleted` summary.
 */
export const PartyDeleteCountsSchema = z.object({
  salesOrders: z.number().int().nonnegative(),
  purchaseOrders: z.number().int().nonnegative(),
  invoices: z.number().int().nonnegative(),
  creditNotes: z.number().int().nonnegative(),
  bills: z.number().int().nonnegative(),
  debitNotes: z.number().int().nonnegative(),
  payments: z.number().int().nonnegative(),
  quotations: z.number().int().nonnegative(),
  posSales: z.number().int().nonnegative(),
  deliveries: z.number().int().nonnegative(),
  recurringInvoices: z.number().int().nonnegative(),
  installmentPlans: z.number().int().nonnegative(),
  journalEntries: z.number().int().nonnegative(),
  stockMovements: z.number().int().nonnegative(),
  cashbackLedger: z.number().int().nonnegative(),
  productReviews: z.number().int().nonnegative(),
  couponRedemptions: z.number().int().nonnegative(),
  attachments: z.number().int().nonnegative(),
  customerNotifications: z.number().int().nonnegative(),
  crmLinks: z.number().int().nonnegative(),
  otpVerifications: z.number().int().nonnegative(),
});
export type PartyDeleteCounts = z.infer<typeof PartyDeleteCountsSchema>;

export const PartyDeletePreviewSchema = z.object({
  partyId: z.string().uuid(),
  code: z.string(),
  displayName: z.string(),
  /** Whether the party is still active — purge is refused (409) until this is false. */
  isActive: z.boolean(),
  counts: PartyDeleteCountsSchema,
  /** The customer has a portal login (lives in the platform DB, purged post-commit). */
  portalLogin: z.boolean(),
  /** Cashback wallet balance in base currency (string), null when no wallet row. */
  cashbackBalance: z.string().nullable(),
  /** Outstanding AR per currency (issued invoices' total − amount_paid). */
  outstandingDebts: z.array(z.object({ currencyCode: z.string(), amount: z.string() })),
});
export type PartyDeletePreview = z.infer<typeof PartyDeletePreviewSchema>;

/**
 * A pending "customer type" request — a self-registered customer awaiting price-list
 * approval.
 *
 * Everything past `createdAt` exists for the REVIEW: approving moves this shopper onto a
 * wholesale tier, and a name plus a phone number is not enough to tell a real ماركيت from
 * someone who ticked the box for the cheaper prices. The trading history is the honest
 * signal, so it travels with the request rather than making staff open another page.
 */
export const PriceListRequestSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  displayName: z.string(),
  phone: z.string().nullable(),
  requestedPriceListId: z.string().uuid(),
  requestedPriceListName: z.string(),
  createdAt: z.string().datetime(),
  // ── review context ──
  email: z.string().nullable(),
  address: z.string().nullable(),
  /** The tier they are on TODAY — approving replaces it, so it has to be visible. */
  currentPriceListName: z.string().nullable(),
  /** Whether they hold a storefront login (a guest checkout has none). */
  hasPortalLogin: z.boolean(),
  /** Do they actually trade with us? */
  orderCount: z.number().int(),
  lastOrderAt: z.string().datetime().nullable(),
  /** Lifetime order value, per currency — never summed across currencies. */
  orderTotals: z.array(z.object({ currencyCode: z.string(), amount: z.string() })),
});
export type PriceListRequest = z.infer<typeof PriceListRequestSchema>;

const LATITUDE = z.number().min(-90).max(90);
const LONGITUDE = z.number().min(-180).max(180);

export const PartyCreateSchema = z.object({
  code: z.string().min(1).max(64),
  displayName: z.string().min(1).max(200),
  partyType: PartyTypeSchema.default('customer'),
  email: z.string().email().optional(),
  phone: z.string().max(32).optional(),
  billingAddress: AddressSchema.optional(),
  shippingAddress: AddressSchema.optional(),
  taxId: z.string().max(64).optional(),
  paymentTermsDays: z.number().int().min(0).max(365).default(30),
  creditLimit: z.string().regex(/^-?\d+(\.\d{1,4})?$/).optional(),
  creditCurrencyCode: z.string().length(3).regex(/^[A-Z]{3}$/).optional(),
  arAccountId: z.string().uuid().optional(),
  apAccountId: z.string().uuid().optional(),
  latitude: LATITUDE.optional(),
  longitude: LONGITUDE.optional(),
  priceListId: z.string().uuid().optional(),
  /**
   * Home branch. Omitted/null by an unrestricted user = shared; a user confined
   * to branches always lands the customer on one of theirs (one branch is
   * filled in for them, several means they must say which).
   */
  warehouseId: z.string().uuid().nullable().optional(),
});
export type PartyCreate = z.infer<typeof PartyCreateSchema>;

export const PartyUpdateSchema = z.object({
  displayName: z.string().min(1).max(200).optional(),
  partyType: PartyTypeSchema.optional(),
  email: z.string().email().nullable().optional(),
  phone: z.string().max(32).nullable().optional(),
  billingAddress: AddressSchema.nullable().optional(),
  shippingAddress: AddressSchema.nullable().optional(),
  taxId: z.string().max(64).nullable().optional(),
  paymentTermsDays: z.number().int().min(0).max(365).optional(),
  creditLimit: z.string().regex(/^-?\d+(\.\d{1,4})?$/).nullable().optional(),
  creditCurrencyCode: z.string().length(3).regex(/^[A-Z]{3}$/).nullable().optional(),
  arAccountId: z.string().uuid().nullable().optional(),
  apAccountId: z.string().uuid().nullable().optional(),
  latitude: LATITUDE.nullable().optional(),
  longitude: LONGITUDE.nullable().optional(),
  priceListId: z.string().uuid().nullable().optional(),
  isActive: z.boolean().optional(),
  /** Move the customer to a branch, or null = share with every branch. Unrestricted users only. */
  warehouseId: z.string().uuid().nullable().optional(),
});
export type PartyUpdate = z.infer<typeof PartyUpdateSchema>;
